import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import api from '@/lib/api';
import MobileShell from '@/components/MobileShell';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import useProviderLocationHeartbeat from '@/hooks/useProviderLocationHeartbeat';
import useServiceStatus from '@/hooks/useServiceStatus';
import ProviderCancelBooking from '@/components/booking/ProviderCancelBooking';
import { formatCurrency, formatTimerSeconds } from '@/lib/serviceTimer';

const BOOKING_POLL_MS = 10000;

export default function ProviderBookingDetail() {
  const { bookingId } = useParams();
  const navTo = useNavigate();
  const [booking, setBooking] = useState(null);
  const [starting, setStarting] = useState(false);
  const [arriving, setArriving] = useState(false);
  const [otp, setOtp] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const [completing, setCompleting] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/bookings/${bookingId}`);
    setBooking(data.booking);
  };

  useEffect(() => {
    load().catch(() => toast.error('Could not load booking'));
  }, [bookingId]);

  // While the booking is still awaiting payment, poll so that when the
  // customer completes payment (status -> confirmed) the Start Trip flow
  // appears without the provider leaving/re-entering this screen.
  useEffect(() => {
    if (booking?.status !== 'payment_pending') return undefined;
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/bookings/${bookingId}`);
        if (!cancelled && data.booking) {
          setBooking(data.booking);
        }
      } catch {
        // Transient failures must not clear the current booking view.
      }
    };
    const timer = setInterval(poll, BOOKING_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [bookingId, booking?.status]);

  const travelStatus = booking?.travel_status;
  const inService = booking?.status === 'in_progress';
  const needsBusyHeartbeat =
    travelStatus === 'en_route' || travelStatus === 'arrived' || inService;
  useProviderLocationHeartbeat(needsBusyHeartbeat ? 'busy' : null);

  const serviceEnabled = booking?.status === 'confirmed' || inService;
  const { service, timer, extraView } = useServiceStatus(bookingId, serviceEnabled);

  const startTrip = async () => {
    setStarting(true);
    try {
      const { data } = await api.post(`/bookings/${bookingId}/start-trip`);
      setBooking((prev) => ({
        ...prev,
        travel_status: data.booking.travel_status,
        trip_started_at: data.booking.trip_started_at,
      }));
      toast.success('Trip started — location sharing active');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start trip');
    } finally {
      setStarting(false);
    }
  };

  const markArrived = async () => {
    setArriving(true);
    try {
      const { data } = await api.post(`/bookings/${bookingId}/arrive`);
      setBooking((prev) => ({
        ...prev,
        travel_status: data.booking.travel_status,
        arrived_at: data.booking.arrived_at,
      }));
      toast.success('Marked as arrived');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not mark arrival');
    } finally {
      setArriving(false);
    }
  };

  const verifyStartOtp = async () => {
    setVerifying(true);
    try {
      const { data } = await api.post(`/bookings/${bookingId}/start-otp/verify`, { otp });
      setBooking((prev) => ({
        ...prev,
        status: data.booking.status,
        service_started_at: data.booking.service_started_at,
        service_scheduled_end_at: data.booking.service_scheduled_end_at,
      }));
      setOtp('');
      toast.success('Service started');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Invalid Start OTP');
    } finally {
      setVerifying(false);
    }
  };

  const finishService = async () => {
    setFinishing(true);
    try {
      const { data } = await api.post(`/bookings/${bookingId}/finish`);
      toast.success(
        data.extra_payment_required
          ? 'Service finished — awaiting customer extra payment'
          : 'Service finished — tap Complete Service when ready',
      );
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not finish service');
    } finally {
      setFinishing(false);
    }
  };

  const completeService = async () => {
    setCompleting(true);
    try {
      await api.post(`/bookings/${bookingId}/complete`);
      toast.success('Service completed');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not complete service');
    } finally {
      setCompleting(false);
    }
  };

  const navigate = () => {
    if (booking.navigation_url) {
      window.open(booking.navigation_url, '_blank', 'noopener,noreferrer');
      return;
    }
    const dest = booking?.service_location;
    if (!dest?.coordinates) return;
    const [lng, lat] = dest.coordinates;
    openNavigation(lat, lng);
  };

  if (!booking) {
    return (
      <MobileShell title="Booking">
        <div className="p-5 text-sm text-slate-500">Loading…</div>
      </MobileShell>
    );
  }

  const paymentConfirmed = booking.payment?.status === 'paid' || booking.status === 'confirmed' || inService;
  const serviceFinished = Boolean(booking.service_ended_at);
  const isCompleted = booking.status === 'completed';
  const canComplete =
    inService &&
    serviceFinished &&
    (booking.settlement_status === 'ready_to_complete' || booking.settlement_status === 'payment_paid');
  const paymentReceived = booking.settlement_status === 'payment_paid';

  return (
    <MobileShell title={isCompleted ? 'Service completed' : inService ? 'Service in progress' : 'Confirmed booking'}>
      <div className="px-5 py-5 space-y-4" data-testid="provider-booking-detail">
        <div className="resqly-card p-4 space-y-2">
          <div className="font-semibold text-slate-900 capitalize">
            {booking.service_key?.replace(/_/g, ' ')}
          </div>
          {booking.customer_name && (
            <div className="text-sm text-slate-600">Customer: {booking.customer_name}</div>
          )}
          <div className="text-sm text-slate-600">{booking.service_address}</div>
          <div className="text-xs">
            <span
              className={`font-semibold px-2 py-0.5 rounded-full ${
                paymentConfirmed ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'
              }`}
            >
              Payment: {paymentConfirmed ? 'Confirmed' : 'Pending'}
            </span>
          </div>
          {travelStatus === 'en_route' && (
            <div data-testid="provider-en-route" className="text-sm font-semibold text-blue-700">
              EN ROUTE — location sharing active
            </div>
          )}
          {travelStatus === 'arrived' && !inService && (
            <div data-testid="provider-arrived" className="text-sm font-semibold text-emerald-700">
              ARRIVED — enter customer Start OTP
            </div>
          )}
        </div>

        {(inService || serviceFinished) && (timer || extraView?.mode === 'frozen') && (
          <div className="resqly-card p-4 space-y-2" data-testid="provider-service-timer">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              {service?.service_phase === 'finished' ? 'SERVICE FINISHED' : timer?.phase === 'extra_time' ? 'EXTRA TIME' : 'SERVICE IN PROGRESS'}
            </div>
            {timer?.phase === 'normal_time' && !serviceFinished && (
              <>
                <div className="text-3xl font-bold text-slate-900 tabular-nums">
                  {formatTimerSeconds(timer.displaySeconds)}
                </div>
                <div className="text-xs text-slate-500">Normal time remaining</div>
              </>
            )}
            {timer?.phase === 'extra_time' && extraView?.mode === 'buffer' && !serviceFinished && (
              <>
                <div className="text-3xl font-bold text-slate-900 tabular-nums" data-testid="provider-buffer-timer">
                  {formatTimerSeconds(extraView.bufferRemainingSeconds)}
                </div>
                <div className="text-xs text-slate-500">Free buffer remaining</div>
              </>
            )}
            {timer?.phase === 'extra_time' && extraView?.mode === 'chargeable' && !serviceFinished && (
              <div data-testid="provider-extra-charge" className="space-y-2">
                <div className="text-sm text-slate-600">
                  Chargeable extra time: {formatTimerSeconds(extraView.chargeableSeconds)}
                </div>
                <div className="text-sm text-slate-600">
                  Tax{extraView.extraTaxPercent != null ? ` (${extraView.extraTaxPercent}%)` : ''}:
                  {formatCurrency(extraView.extraTaxAmount ?? 0, extraView.currency)}
                </div>
                <div className="text-lg font-bold text-slate-900">
                  Current extra charge: {formatCurrency(extraView.extraTotalAmount, extraView.currency)}
                </div>
              </div>
            )}
            {extraView?.mode === 'frozen' && (
              <div data-testid="provider-frozen-settlement" className="space-y-2">
                <div className="text-sm text-slate-600">
                  Final chargeable extra: {formatTimerSeconds(extraView.chargeableSeconds ?? 0)}
                </div>
                <div className="text-sm text-slate-600">
                  Tax{extraView.extraTaxPercent != null ? ` (${extraView.extraTaxPercent}%)` : ''}:
                  {formatCurrency(extraView.extraTaxAmount ?? 0, extraView.currency)}
                </div>
                <div className="text-lg font-bold text-slate-900">
                  Final extra charge: {formatCurrency(extraView.extraTotalAmount, extraView.currency)}
                </div>
                {booking.extra_payment_required && !booking.extra_payment_completed_at && (
                  <div className="text-xs text-amber-700">Awaiting customer extra payment</div>
                )}
                {paymentReceived && (
                  <div data-testid="provider-payment-received" className="text-sm font-semibold text-emerald-700">
                    PAYMENT RECEIVED
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {inService && !serviceFinished && (
          <Button
            data-testid="provider-finish-service-btn"
            className="w-full bg-emerald-700 hover:bg-emerald-800"
            disabled={finishing}
            onClick={finishService}
          >
            {finishing ? 'Finishing…' : 'Finish Service'}
          </Button>
        )}

        {canComplete && (
          <Button
            data-testid="provider-complete-service-btn"
            className="w-full bg-slate-900 hover:bg-slate-800"
            disabled={completing}
            onClick={completeService}
          >
            {completing ? 'Completing…' : 'Complete Service'}
          </Button>
        )}

        {booking.status === 'confirmed' && (
          <div className="space-y-2">
            <Button
              data-testid="provider-navigate-btn"
              variant="outline"
              className="w-full"
              onClick={navigate}
            >
              Navigate
            </Button>
            {travelStatus === 'not_started' && (
              <Button
                data-testid="provider-start-trip-btn"
                className="w-full bg-slate-900 hover:bg-slate-800"
                disabled={starting}
                onClick={startTrip}
              >
                {starting ? 'Starting…' : 'Start Trip'}
              </Button>
            )}
            {travelStatus === 'en_route' && (
              <Button
                data-testid="provider-arrive-btn"
                className="w-full bg-blue-700 hover:bg-blue-800"
                disabled={arriving}
                onClick={markArrived}
              >
                {arriving ? 'Updating…' : "I've Arrived"}
              </Button>
            )}
            {travelStatus === 'arrived' && (
              <div className="space-y-2" data-testid="provider-start-otp-form">
                <label className="text-sm text-slate-600">Enter customer&apos;s Start OTP</label>
                <input
                  data-testid="provider-otp-input"
                  inputMode="numeric"
                  maxLength={6}
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                  className="w-full border rounded-lg px-3 py-2 text-lg tracking-[0.4em] text-center"
                  placeholder="······"
                />
                <Button
                  data-testid="provider-start-service-btn"
                  className="w-full bg-slate-900 hover:bg-slate-800"
                  disabled={verifying || otp.length !== 6}
                  onClick={verifyStartOtp}
                >
                  {verifying ? 'Starting…' : 'Start Service'}
                </Button>
              </div>
            )}
          </div>
        )}

        {booking?.customer_phone && booking.status !== 'completed' && booking.status !== 'cancelled' && !booking.service_ended_at && (
          <PhoneContact
            bookingId={booking.id}
            phone={booking.customer_phone}
            contactedAt={booking.phone_contacted_at}
            onContacted={(at) => setBooking((prev) => ({ ...prev, phone_contacted_at: at }))}
            label="Call Customer"
          />
        )}

        {!isCompleted && (
          <ProviderCancelBooking booking={booking} onCancelled={() => navTo('/provider/dashboard')} />
        )}

        <Link to="/provider/dashboard" className="block text-center text-sm text-blue-700 font-semibold py-2">
          Back to Dashboard
        </Link>
      </div>
    </MobileShell>
  );
}


function PhoneContact({ bookingId, phone, contactedAt, onContacted, label }) {
  const [loading, setLoading] = useState(false);
  const contact = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const { data } = await api.post(`/bookings/${bookingId}/phone-contact`);
      onContacted?.(data.phone_contacted_at);
      window.location.href = `tel:${phone}`;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not enable calling');
    } finally { setLoading(false); }
  };
  if (!phone) return null;
  return (
    <div className="resqly-card p-4 space-y-2">
      <div className="text-xs font-semibold text-slate-500 uppercase">Service contact</div>
      <button type="button" className="w-full text-left" onClick={contact} disabled={loading} data-testid="provider-customer-phone">
        <div className="text-sm text-slate-500">{label}</div>
        <div className="font-semibold text-blue-700">{loading ? 'Opening phone…' : phone}</div>
      </button>
      {!contactedAt && <div className="text-[11px] text-amber-700">Calling disables cancellation for both participants.</div>}
    </div>
  );
}
